import * as React from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Collapse, CssBaseline, ListSubheader } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './CorporateSideBar.css';
import { infectionControlIcon, medicationIcon, oshaHipaaIcon } from '../icons';
import constants from '../constants';
import { Link, useNavigate } from 'react-router-dom';
import { HFSubMenu } from '../themes/components/ListItemButton';
import { useAppSelector } from '../redux/store';
import { UserAccessLevels, selectCurrentUser } from '../redux/user';
import { getEPayLink } from '../services/nodusService';
import { selectBalance, selectCustomerNumber, selectShowAutoclaveConfirmationAlertForCorp } from '../redux/customer';
import { addError } from '../redux/alerts';
import { useAppDispatch } from '../redux/store';
import { LightTooltip } from './ToolTip.styled';

export default function CorporateSidebar() {
  const isDemoEnvironment = constants.ENV.toLowerCase() === 'demo';
  const dispatch = useAppDispatch();
  const [drawerName, setOpen] = React.useState('');
  const navigate = useNavigate();
  const drawerWidth = 280;

  const currentUser = useAppSelector(selectCurrentUser);
  const customerNumber = useAppSelector(selectCustomerNumber);
  const showAutoclaveConfirmation = useAppSelector(selectShowAutoclaveConfirmationAlertForCorp)

  const handleDropdown = (name?: string) => {
    if (name === drawerName) {
      setOpen('');
    } else {
      setOpen(name || '');
    }
  };

  const hasPermission = (permissions: UserAccessLevels[]): boolean => permissions.some((perm) => perm === currentUser?.access_level);
  const openLinkToEPayPayments = async (): Promise<void> => {
    try {
      const link = await getEPayLink(customerNumber, 'payments');
      window.open(link, '_blank');
    } catch {
      dispatch(addError('Failed to open link'));
    }
  };
  const balance = useAppSelector(selectBalance);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' }
          }}
          open
        >
          <List subheader={<ListSubheader>Organization</ListSubheader>}>
            <ListItem key="Dashboard" disablePadding>
              <ListItemButton component={Link} to="/corp/dashboard">
                <FontAwesomeIcon icon="gauge-high" className="sidebar-icon" />
                <ListItemText primary="Dashboard" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            {hasPermission([UserAccessLevels.Admin, UserAccessLevels.Owner]) && (
              showAutoclaveConfirmation && (
                <ListItem key="Autoclave Confirmation Status" disablePadding>
                  <ListItemButton component={Link} to="/corp/biological/autoclave-confirmation-status">
                    <FontAwesomeIcon icon="square-check" className="sidebar-icon" />
                    <ListItemText primary="Autoclave Confirmation Status" disableTypography className="sidebar-text" />
                  </ListItemButton>
                </ListItem>
              ))}
            {hasPermission([UserAccessLevels.Admin, UserAccessLevels.Owner]) && (
              <ListItem key="Pay Your Balance" disablePadding>
                <ListItemButton onClick={() => openLinkToEPayPayments()}>
                  <FontAwesomeIcon icon={['far', 'credit-card']} className="sidebar-icon" />
                  <ListItemText primary="Pay Your Balance" disableTypography className="sidebar-text" />
                  <LightTooltip placement="top" title={balance ? `$${balance}` : '$0.00'}>
                    <FontAwesomeIcon icon="circle-info" className="gray-icon" />
                  </LightTooltip>
                </ListItemButton>
              </ListItem>
            )}
            <ListItem key="Practices" disablePadding>
              <ListItemButton
                component="a"
                onClick={() => {
                  navigate('/corp/practices');
                }}
              >
                <FontAwesomeIcon icon="building" className="sidebar-icon" />
                <ListItemText primary="Practices" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Groups" disablePadding>
              <ListItemButton component="a" onClick={() => navigate('/corp/groups')}>
                <FontAwesomeIcon icon="users" className="sidebar-icon" />
                <ListItemText primary="Groups" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Team Members" disablePadding>
              <ListItemButton component="a" onClick={() => navigate('/practice/team-members')}>
                <FontAwesomeIcon icon="user" className="sidebar-icon" />
                <ListItemText primary="Team Members" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List subheader={<ListSubheader>Compliance Reports</ListSubheader>}>
            <ListItem key="OSHA/HIPAA" disablePadding>
              <ListItemButton
                component="a"
                onClick={() => {
                  navigate('/corp/osha-hipaa');
                }}
              >
                <div className="alt-icon">
                  <img src={oshaHipaaIcon} alt="" />
                </div>
                <ListItemText primary="OSHA/HIPAA" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Training" disablePadding>
              <ListItemButton
                component="a"
                onClick={() => {
                  navigate('/corp/training');
                }}
              >
                <FontAwesomeIcon icon="graduation-cap" className="sidebar-icon" />
                <ListItemText primary="Training" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
            <ListItem key="Meds & Devices" disablePadding>
              <ListItemButton onClick={() => handleDropdown('meds')}>
                <div className="alt-icon">
                  <img src={medicationIcon} />
                </div>
                <ListItemText primary="Meds & Devices" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'meds' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'meds'}>
              <List component="div" disablePadding>
                <HFSubMenu component="a" onClick={() => navigate('/corp/medications')}>
                  Medications
                </HFSubMenu>
                <HFSubMenu component="a" onClick={() => navigate('/corp/devices')}>
                  Devices
                </HFSubMenu>
              </List>
            </Collapse>
            <ListItem key="Infection Control" disablePadding>
              <ListItemButton onClick={() => handleDropdown('infection')}>
                <div className="alt-icon">
                  <img src={infectionControlIcon} />
                </div>
                <ListItemText primary="Infection Control" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'infection' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'infection'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to={`/corp/biological`}>
                  Biological Monitoring
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/waterline-management">
                  Waterline Management
                </HFSubMenu>
              </List>
            </Collapse>
            <ListItem key="Waste Management" disablePadding>
              <ListItemButton onClick={() => handleDropdown('waste')}>
                <FontAwesomeIcon icon="earth-americas" size="lg" className="sidebar-icon" />
                <ListItemText primary="Waste Management" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'waste' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'waste'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/corp/medical-waste">
                  Sharps & Medical Waste
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/pharmaceutical-waste">
                  Pharmaceutical Waste
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/amalgam-waste">
                  Amalgam Waste
                </HFSubMenu>
              </List>
            </Collapse>
          </List>
          <Divider />
          <List>
            <ListItem key="Store" disablePadding>
              <ListItemButton component="a" target="_blank" href={constants.HEALTHFIRST_WEB_URL}>
                <FontAwesomeIcon icon="cart-shopping" className="sidebar-icon" />
                <ListItemText primary="Store" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List subheader={<ListSubheader>Documents</ListSubheader>}>
            <ListItem key="SDS" disablePadding>
              <ListItemButton component={Link} to='/corp/sds'>
                <FontAwesomeIcon icon={['far', 'folder-open']} className="sidebar-icon" />
                <ListItemText primary="SDS" disableTypography className="sidebar-text" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem key="Help Center" disablePadding>
              <ListItemButton onClick={() => handleDropdown('help')}>
                <FontAwesomeIcon icon="circle-question" className="sidebar-icon" />
                <ListItemText primary="Help Center" disableTypography className="sidebar-text" />
                <FontAwesomeIcon icon={drawerName === 'help' ? 'chevron-down' : 'chevron-right'} size="xs" className="arrow-icon" />
              </ListItemButton>
            </ListItem>
            <Collapse in={drawerName === 'help'}>
              <List component="div" disablePadding>
                <HFSubMenu component={Link} to="/corp/contact">
                  Contact Us
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/faq">
                  FAQ
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/terms">
                  Terms & Conditions
                </HFSubMenu>
                <HFSubMenu component={Link} to="/corp/privacy-policy">
                  Privacy Policy
                </HFSubMenu>
                <HFSubMenu>Take the Tour</HFSubMenu>
                <HFSubMenu component={Link} to="/corp/guides">
                  Video Guides
                </HFSubMenu>
              </List>
            </Collapse>
            {isDemoEnvironment && (
              <ListItem key="Demo" disablePadding>
                <ListItemButton component={Link} to="/corp/demo">
                  <FontAwesomeIcon icon="star" className="sidebar-icon" />
                  <ListItemText primary="Demo" disableTypography className="sidebar-text" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Drawer>
      </Box>
    </Box>
  );
}
